/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  Vue, Component, Inject, Watch, Prop,
} from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { Popup } from 'vant';
import type { ApplyChannelData, ApplyProductData, CalcPlanListData } from '@/api/apply-product/types';
import { RateTypeEnum } from '@/api/apply-product/types';
import FormField from '@/components/form-field';
import { repayPlanModelType } from '@/po/ProductResult';
import { rightMonitorReport } from '@/shared/right-utils/rightMonitor';
import { getGalileoAb } from '@/shared/galileoAb';
import { ABResEnum } from '@/enums/abExperiment';
import { Tag } from '@hb/coffee';
import { getShowRateValue, RateTypeNames } from '@/components/finance-replan/until';
import { PeriodOption } from '../../types';
import { displayLoadingText } from '../../product';
import PeriodSelectView from './components/period-select-view/index.vue';

const style = require('./index.module.scss');

const ApplyProductStore = namespace('applyProduct');
const LoanRightStore = namespace('loanRight');

@Component
export default class SelectPeriod extends Vue {
  @ApplyProductStore.State channel!: ApplyChannelData;

  @ApplyProductStore.State period!: PeriodOption | undefined;

  @ApplyProductStore.State amount!: string;

  @ApplyProductStore.State periodList!: PeriodOption[];

  @ApplyProductStore.State product!: ApplyProductData

  @ApplyProductStore.State planListData!: CalcPlanListData;

  @ApplyProductStore.State multiChannelRepayment!: string;

  @ApplyProductStore.Getter needBuyRight!: boolean;

  @ApplyProductStore.Getter showCouponRetainPopupExperiment!: number;

  @ApplyProductStore.Getter isSimplifiedListStyle!: boolean;

  @ApplyProductStore.Action $updateState!: (payload: Dictionary) => void;

  @ApplyProductStore.Action getCouponList!: () => Promise<void>;

  @ApplyProductStore.Action calcRepayPlans!: (
    payload?: {
      repayType: string;
      currentAmount: string;
    }
  ) => Promise<void>;

  @LoanRightStore.Action getRightSummaryData!: () => Promise<void>;

  @Inject() readonly registValidator!: (validator: () => string | void) => void;

  private isActionSheetVisible = false;

  private selectPeriodExperiment = false;

  @Watch('period')
  private onPeriodChange() {
    this.calcRepayPlans({
      repayType: repayPlanModelType[this.currentRepayType],
      currentAmount: this.amount,
    });
  }

  private setShowPeriodSelect(show: boolean) {
    this.isActionSheetVisible = !!show;
  }

  private get currentRepaymentLength() {
    return this.currentProductRepayMode.length;
  }

  private get currentProductRepayMode(): string[] {
    return (this.product && this.product.productInfo && this.product.productInfo.repaymentModel) || [];
  }

  private get currentRepayType(): string | number {
    if (!this.multiChannelRepayment) {
      return this.currentRepaymentLength && this.currentProductRepayMode[0];
    }
    return this.multiChannelRepayment;
  }

  private async created() {
    // eslint-disable-next-line consistent-return
    this.registValidator(() => {
      if (!(this.period && this.period.period)) {
        return '请选择还款期限';
      }
    });

    /** 初始长周期挽留实验的分流结果 */
    await this.initLongPeriodRetentionExperiment();
  }

  private async initLongPeriodRetentionExperiment() {
    /**
     * 伽利略实验：https://ab.hellobike.cn/experiment/detail/202508041147079672?isDetail=true
     * 实验名称：还款期限选择实验
     * 实验目的：测试引导/不引导长周期的提现转化效果
     */
    const abRes = await getGalileoAb().getAbResCatchError('202508041147079672', '202508041147079755');

    this.selectPeriodExperiment = abRes?.result === ABResEnum.HINT;
  }

  /**
   * 渲染利率内容
   * @returns 利率内容
   */
  private get rateContent(): string {
    const { rateType = '' } = this.channel || {};
    const { rateType: rateDateType, rateValue = 0 } = this.planListData || {}; // 合并空值处理
    const rateShowText = rateDateType ? RateTypeNames[rateDateType] : '';
    if (rateDateType && rateValue && rateType !== RateTypeEnum.CAPITAL_TYPE_36) {
      return `${rateShowText}利率${this.product?.productInfo?.calcMethod ? '(单利)' : ''}${getShowRateValue(rateValue)}%`;
    }
    return '';
  }


  private render() {
    /** 是否显示引导“长周期”期限选择-选择组件 */
    const showLongPeriodRecommend = this.selectPeriodExperiment && this.periodList?.length && this.channel?.showPeriodRecommend;
    const { couponDiscountRate } = this.planListData || {};

    if (couponDiscountRate && couponDiscountRate > 0) {
      this.$ubtModuleExposeNew('finance_loan_apply_couponDiscountRate_firstScreen', {
        couponDiscountRate,
      });
    }

    return (
      <div>
        <FormField
          name={this.isSimplifiedListStyle ? '借多久' : '还款期限'}
          nameLoading={this.isSimplifiedListStyle === null}
          onClick={this.showPeriodList.bind(this)}
        >
          {this.period && this.amount
            ? (
              <div class={{ [style.initHeight]: this.showCouponRetainPopupExperiment && !this.planListData }}>
                <span>
                  {this.period.value}{this.period.unitName}
                  {(this.isSimplifiedListStyle && this.rateContent) ? `，${this.rateContent}` : ''}
                </span>
                {(this.isSimplifiedListStyle && couponDiscountRate && couponDiscountRate > 0) && (
                  <div class={style.couponDiscountRateTips}>
                    用券后综合年利率降至
                    <Tag class={style.couponDiscountRate} type="primary" highlight>
                      {getShowRateValue(couponDiscountRate, 2)}%
                    </Tag>
                  </div>
                )}
                {!this.isSimplifiedListStyle && <div class={style.periodTips}>期限越长，还款压力越小</div>}
              </div>
            )
            : <p class={[style.periodLoading, this.isSimplifiedListStyle ? style.noMargin : '']}>{displayLoadingText}</p>
          }
        </FormField>

        {showLongPeriodRecommend ? (
          <PeriodSelectView
            visible={this.isActionSheetVisible}
            isSimplifiedListStyle={this.isSimplifiedListStyle}
            onUpdateVisible={this.setShowPeriodSelect.bind(this)}
            onUpdateSelectPeriod={this.onPeriodSelect.bind(this)}
            periodList={this.periodList}
            currentSelectPeriod={this.period}
            rateContent={this.rateContent}
            onCancel={this.hidePeriodList.bind(this)}
            onConfirm={this.showPeriodList.bind(this)}
          />
        ) : (
          <Popup
            position="bottom"
            class={style.popupContent}
            v-model={this.isActionSheetVisible}
          >
            <div class={style.header}>请选择还款期限</div>

            <div class={style.content}>
              {this.periodList.map((item) => (
                <div
                  class={[style.periodOption, 'font-din', 'van-hairline--top']}
                  onClick={this.onPeriodSelect.bind(this, item)}
                >
                  <span class="font-din">{item.value}</span>
                  {item.unitName}
                </div>
              ))}
            </div>

            <div class={style.actionButton} onClick={this.hidePeriodList.bind(this)}>
              取消
            </div>
          </Popup>
        )}
      </div>
    );
  }

  private showPeriodList() {
    this.$ubtPageButton(
      '臻有钱_提现申请_显示期限列表',
      '产品',
      this.channel && this.channel.productId,
      'selectPeriodExperiment',
      String(this.selectPeriodExperiment),
    );

    this.isActionSheetVisible = true;
  }

  private hidePeriodList() {
    this.$ubtPageButton(
      '臻有钱_提现申请_隐藏期限列表',
      '产品',
      this.channel && this.channel.productId,
    );

    this.isActionSheetVisible = false;
  }

  private onPeriodSelect(period: PeriodOption) {
    this.$ubtPageButton(
      '臻有钱_提现申请_选择期限',
      '产品',
      this.channel && this.channel.productId,
    );

    this.$updateState({
      period,
    });

    // 监控埋点，用来辅助判断是否已经进行了期数切换时，权益查询接口调用判断是否生效
    rightMonitorReport(
      'click_select_period',
      {
        needBuyRight: this.needBuyRight,
        period,
        productId: this.channel?.productId,
        capitalCode: this.channel?.capitalCode,
      },
    );


    this.isActionSheetVisible = false;
    this.getCouponList();
  }
}
